import * as $ from 'jquery'
import { Div, Variable, ElementProperties, TextInput, ElementRenderer, ReplacedEvent } from 'alkali'
import Feedback from 'components/generic/Feedback'
import * as _ from 'lodash'
import 'typeahead.js'
import { sanitize } from 'lib/util/StringUtils'

const TEXT_INPUT_CHANGE_EVENTS = [
	'propertychange',
	'change',
	'click',
	'keyup',
	'input',
	'paste',
	'blur',

	// 'blurred',
	// 'focused',
	// 'queryChanged',
	// 'whitespaceChanged',
	// 'selectableClicked',
	// 'typeahead:active',
	// 'typeahead:open',
	// 'typeahead:close',
	// 'typeahead:change',
	'typeahead:autocomplete',
	'typeahead:autocompleted',
	'typeahead:select',
	'typeahead:selected',
	// 'mousedown.tt'
]

export interface MenuItem {
	label: string
	title?: string
}

export interface SuggestionSource<T> {
	itemToText(item: T): string
	itemToEntry?(item: T): MenuItem
	findMatches(query: string): Promise<T[]>
	findExact(val: string): Promise<T>
}

export interface TypeAheadInputOpts<T> extends ElementProperties<TypeAheadInput<T>> {
	placeholder?: string
	inputEvents?: string[]
	source: SuggestionSource<T>
}

class RendererWithEvent<T> extends ElementRenderer<T> {
	protected updateEvent: any
	updated(updateEvent, ...rest) {
		this.updateEvent = updateEvent
		super.updated(updateEvent, ...rest)
	}
}

/**
 * Alkali and TypeAhead each, independently, update internal variable based on input event handler binding
 * (which both source from and sink to input value)
 * However, events that set the respective internal variables directly, which do not trigger respective event handlers,
 * must explicitly cause a corresponding update in the other side of the relationship.
 * We must try to avoid this propagation when the component is in an updating/invalidated state and may be removed from the UI.
 *
 * In addition, we must be able to externally assign/initialize a text value and object value simultaneously and avoid implicit object value lookup
 */
class TypeAheadState<T> {
	constructor(private readonly text: Variable<string>,
	            private readonly value: Variable,
	            private readonly disabled = false,
	            private readonly suggestionSource: SuggestionSource<T>,
	            private readonly element,
	            private readonly updateTA: (v) => void,
	            private readonly onAssignValue?: (v) => void) {
		let last = text.valueOf()
		let pending
		const lookupItem = _.debounce(val => {
			const request = pending = suggestionSource.findExact(val).then(result => {
				if (pending == request) {
					pending = null
					//if (document.body.contains(element)) this.assignValue(result)
				}
			})
		}, 200)
		new RendererWithEvent<string>({
			variable: text,
			element,
			renderUpdate(newValue) {
				if (last != newValue) {
					// if (this.updateEvent?.origin !== 'TypeAhead') {
					// 	updateTA(newValue)
					// }
					if (!this.updateEvent?.reassigning) {
						// 	// if the text variable has changed explicitly update the typeahead text value
						// 	this.setValue(newValue)
						// then look up an exact match to assign as value object
						lookupItem.cancel()
						pending = null
						lookupItem(newValue)
					}
					// ensure last value is *always* updated
					last = newValue
				}
				this.updateEvent = null
			}
		})
	}

	assignTAValueAndText(value: T) {
		this.assignValueAndText(value, 'TypeAhead')
	}

	assignValueAndText(value: T, origin?: string) {
		const event = new ReplacedEvent()
		event.origin = origin
		event.reassigning = true
		const text = value ? this.suggestionSource.itemToText(value) : undefined
		this.text.put(text)
		//this.setValue(text)
		this.assignValue(value)
	}

	assignValue(v: T) {
		this.value.put(v)
		this.onAssignValue?.(v)
	}
}

class TypeAheadInput<T> extends TextInput('.form-control', { inputEvents: TEXT_INPUT_CHANGE_EVENTS }) {
	static readonly UNSPECIFIED_VALUE = 'Unspecified'

	contentVar: Variable<string>
	valueVar: Variable<any>

	private source: SuggestionSource<T>
	private _typeahead: any
	private _feedback: Feedback
	private disabled: any
	private hasFeedback: boolean
	private onAssignValue: (v: any) => void
	private state: TypeAheadState<T>

	created(properties) {
		// caller should pass units as property
		this.contentVar = properties.content
		this.source = properties.source
		this.valueVar = properties.valueVar || new Variable()
		let initialValue = this.valueVar.valueOf()
		let initialContent = this.contentVar.valueOf()
		if (initialValue && !initialContent) {
			this.contentVar.put(this.source.itemToText(initialValue))
		} else if (initialContent && !initialValue) {
			this.valueVar.put(this.source.findExact(initialContent))
		}
	}

	attached() {
		// ensure only initialized once
		this._typeahead = this._typeahead || (this._connectTypeahead($(this), this.source), true)
		if (this.hasFeedback != false) {
			this._feedback = this._feedback || (() => {
				const f = new Feedback(this.parentElement, (val) => this.classify(this.valueVar.valueOf()), 'input.form-control.tt-input')
				// perform an initial update
				f.update()
				return f
			})()
		}
		this.state = new TypeAheadState(
			this.contentVar,
			this.valueVar,
			this.disabled,
			this.source,
			this, value => {
				//$(this).typeahead('val', value) 
				this.dispatchEvent(new InputEvent('input'))
			},
			this.onAssignValue
		)
		$(this).on('typeahead:select typeahead:autocomplete', (e, suggestion) => {
			this.state.assignTAValueAndText(suggestion)
		})
	}

	/* explictly assign the value object and typeahead text, without initiating an exact match lookup */
	protected updateValue(suggestion) {
		this.state.assignValueAndText(suggestion)
	}

	protected updateValueOnly(suggestion) {
		this.state.assignValue(suggestion)
	}

	// trap explicit value updates that typeahead makes
	// set value(v) {
	// 	super.value = v
	// 	//this.contentVar && this.contentVar.updated()
	// 	//this.valueVar && this.valueVar.updated()
	// 	// must explicitly/eagerly put value here or valueVar observes stale value...timing issue?
	// 	this.contentVar && this.contentVar.put(v)
	// }

	/* explicitly assign the typeahead text value */
	setValue(v) {
		$(this).typeahead('val', v)
	}

	// get value() {
	// 	return super.value
	// }

	private _connectTypeahead(input, source: SuggestionSource<T>) {
		const display = u => source.itemToText(u)
		let lastSuggestions = []
		const itemToEntry = (item => source.itemToEntry(item)) || (u => ({ label: display(u), title: display(u) }))
		let typeAheadInput = this
		$(input).typeahead(null, {
			hint: true,
			display,
			minLength: 3,
			limit: 100,
			source(val, syncResults, asyncResults) {
				if (val.length) {
					lastSuggestions = lastSuggestions.filter(suggestion => !suggestion.downrank)
					if (typeAheadInput.source.addDefaults)
						typeAheadInput.source.addDefaults(lastSuggestions, val)
					for (let suggestion of lastSuggestions)
						suggestion.loading = true
					syncResults(lastSuggestions)
					val = sanitize(val)
				  	source.findMatches(val).then(results => {
				  		this.$lastSuggestion = [] // clear out sync suggestions
				  		asyncResults(lastSuggestions = results)
					})
				}
			},
			templates: {
				suggestion: context => {
					const { label, title, classes } = itemToEntry(context)
					let element = new Div({
						title: title || label,
						content: label,
						classes: classes || {},
					})
					return element
					if (downrank)
						element.addClass('downranked-suggestion')
					return element
				}
			}
		})
	}

	protected classify(value) {
		return value ? 'success' : 'error'
	}

	protected static sortMatches(results, val, source) {
		const startsWith = new RegExp('^' + _.escapeRegExp(val), 'ig')
		return _.orderBy(results, [
			// order by exact match first
			(m) => {
				if (m.downrank)
					return 2
				if (m.score)
					return -m.score
				m = source.itemToText(m)
				return m.length == val.length ? 0 : 1
			},
			// then by prefix
			(m) => { m = source.itemToText(m); startsWith.lastIndex = 0; return startsWith.test(sanitize(m)) ? 0 : 1 },
			// then alphabetically
			(m) => source.itemToText(m),
		])
	}
}

export default TypeAheadInput
